/*
 * D.TAILS Wishlist — theme-native storefront (single-file, no build step).
 * Loaded as an ES module from snippets/wishlist-data.liquid, which also
 * provides the window.wishlistData globals this file reads.
 * lit-all.min.js is the vendored Lit runtime (immutable, hence the relative
 * import — it never changes, so CDN cache staleness cannot bite).
 */
import { LitElement, html, nothing, unsafeHTML, repeat } from './lit-all.min.js'

// ==========================================================================
// Config
// ==========================================================================

const wishlistData = window.wishlistData || {}

// The app proxy subpath must match the [app_proxy] subpath of the wishlist app
// installed on this store (production: dtails-wishlist). It's a theme setting
// because the staging store runs a staging app on a different subpath.
const PROXY_SUBPATH = wishlistData.settings?.proxy_subpath?.trim() || 'dtails-wishlist'

const PROXY_API_URL = `${window.location.protocol}//${window.location.host}/apps/${PROXY_SUBPATH}/proxy`

const shopifyCustomer = wishlistData.liquidData?.customer?.id
  ? wishlistData.liquidData.customer
  : false

const shopifyRoot = window.Shopify.routes.root

const sharedList = wishlistData.sharedList || {}

// ==========================================================================
// Utils (money, translations, images)
// ==========================================================================

// ---- utils.js ----
const money = (price, multiplier, format) => {
  if (multiplier) {
    price = price * multiplier
  }
  return formatMoney(price, format ? `{{${format}}}` : window.wishlistData?.theme?.moneyFormat)
}

const translations = tryParseJSONObject(decodeHTMLEntities(wishlistData.translations).replace(/=>/g, ':')) || {}

function t(translate_string, vars) {
  if (Object.keys(translations).length === 0) return '[Missing translation]'
  let translated_string = translate_string.split('.').reduce((o, i) => o ? o[i] : null, translations)
  // If translated_string is object, it means there's a pluralization key
  if (typeof translated_string === 'object') {
    translated_string = translated_string[vars?.count === 1 ? 'one' : 'other']
  }
  for (const prop in vars) {
    translated_string = translated_string.replace('{{ ' + prop + ' }}', vars[prop])
  }
  return translated_string || '[Missing translation]'
}

function tryParseJSONObject(jsonString) {
  try {
    var o = JSON.parse(jsonString)
    if (o && typeof o === "object") {
      return o
    }
  }
  // eslint-disable-next-line
  catch (e) { }
  return false
}

/**
 * Adds a Shopify size attribute to a URL
 *
 * @param src
 * @param size
 * @param newFormat
 * @returns {*}
 */
function getSizedImageUrl(src, size, newFormat) {
  if (size === null) {
    return src;
  }

  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);

  if (match) {
    const prefix = src.split(match[0]);
    let suffix = match[0];
    const format = match[1]

    if (newFormat && newFormat !== format && format !== 'png') {
      //Only PNG's can be converted by shopify
      return null
    }
    if (newFormat) {
      suffix = suffix.replace(format, `${format}.${newFormat}`)
    }

    const url = size === 'master' ? `${prefix[0]}${suffix}` : `${prefix[0]}_${size}${suffix}`

    return removeProtocol(url);
  } else {
    return null;
  }
}

function removeProtocol(path) {
  return path.replace(/http(s)?:/, '');
}

const default_image_width = 800;
const default_image_sizes = [300, 400, 500, 600, 700, 900, 1200, 1600];

const imageSrc = (image, size) => {
  if (!image || (typeof image === 'object' && !image.url)) return
  const default_size = size || `${default_image_width}x`
  return getSizedImageUrl(image.url || image, default_size)
};

// Thumbnail for a saved item: the saved variant's own image, else the product's
// main image — the same rule as the theme card pinned to that variant
// (snippets/product-card.liquid), so the lists overview, the "saved to" notice
// and the add-to-list rows never show a different photo than the card inside
// the list. Returns the URL string: a variant's featured_image is an object
// keyed `src`, which imageSrc() would reject. `variants` is optional-chained
// because an item saved after a failed product fetch carries `data: {}`.
// Alt text stays the product title at every call site — variant image alts
// carry the `_ps` / `_ref` packshot markers only Liquid strips.
const itemImage = (item) =>
  item?.data?.variants?.find(v => v.id == item.variantId)?.featured_image?.src || item?.data?.featured_image

const imageSrcSet = (image, image_sizes = default_image_sizes) => {
  if (!image || (typeof image === 'object' && !image.url)) return
  const max_width = image.width && default_image_width < image.width
    ? Math.round(image.width)
    : default_image_width;

  const sizes = image_sizes.reduce((acc, w) => {
    const sized_width = getSizedImageUrl(image.url || image, `${w}x`);
    return max_width >= w
      ? acc.concat([`${sized_width} ${w}w`])
      : acc;
  }, []);
  return sizes.join(',');
};

/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 */

 const moneyFormat = '${{amount}}';

 /**
  * Format money values based on your shop currency settings
  * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
  * or 3.00 dollars
  * @param  {String} format - shop money_format setting
  * @return {String} value - formatted value
  */
 export function formatMoney(cents, format) {
   if (typeof cents === 'string') {
     cents = cents.replace('.', '');
   }
   let value = '';
   const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
   const formatString = format || moneyFormat;

   function formatWithDelimiters(
     number,
     precision = 2,
     thousands = ',',
     decimal = '.'
   ) {
     if (isNaN(number) || number == null) {
       return 0;
     }

     number = (number / 100.0).toFixed(precision);

     const parts = number.split('.');
     const dollarsAmount = parts[0].replace(
       /(\d)(?=(\d\d\d)+(?!\d))/g,
       `$1${thousands}`
     );
     const centsAmount = parts[1] ? decimal + parts[1] : '';

     return dollarsAmount + centsAmount;
   }

   switch (formatString.match(placeholderRegex)[1]) {
     case 'amount':
       value = formatWithDelimiters(cents, 2);
       break;
     case 'amount_no_decimals':
       value = formatWithDelimiters(cents, 0);
       break;
     case 'amount_with_comma_separator':
       value = formatWithDelimiters(cents, 2, '.', ',');
       break;
     case 'amount_thousand_separator_only':
       value = formatWithDelimiters(cents, 2, '.', ',')
       if (value) {
        value = value.substring(0, value.indexOf(','));
       }
       break;
     case 'amount_no_decimals_with_comma_separator':
       value = formatWithDelimiters(cents, 0, '.', ',');
       break;
   }

   return formatString.replace(placeholderRegex, value);
 }

/**
 * @param {String} HTML representing a single element
 * @return {Element}
 */
function htmlToElement(html) {
  var template = document.createElement('template')
  html = html.trim(); // Never return a text node of whitespace as the result
  template.innerHTML = html
  return template.content.firstElementChild
}

function decodeHTMLEntities (str) {
  var element = document.createElement('div');
  if(str && typeof str === 'string') {
    // strip script/html tags
    str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
    str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '');
    element.innerHTML = str;
    str = element.textContent;
    element.textContent = '';
  } else {
    return ''
  }

  return str;
}

// ==========================================================================
// API (product cards via ?view=wishlist-card, proxy saves)
// ==========================================================================

// One fetch per product serves both worlds: templates/product.wishlist-card.liquid
// returns the theme's real product card markup plus a JSON data island with the
// product data the store needs. Cached per handle+variant for the session.
const viewCache = new Map()

const fetchProductView = (handle, variantId) => {
  const key = `${handle}:${variantId || ''}`
  if (!viewCache.has(key)) {
    const promise = (async () => {
      const params = new URLSearchParams({ view: 'wishlist-card' })
      if (variantId) params.set('variant', variantId)
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}?${params}`)
      if (!response.ok) {
        const error = new Error(`Failed to fetch product ${handle} (${response.status})`)
        error.status = response.status
        error.handle = handle
        throw error
      }
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html')
      const dataElement = doc.querySelector('[data-wishlist-product]')
      const data = dataElement ? JSON.parse(dataElement.textContent) : null
      dataElement?.remove()
      return { data, cardHtml: doc.body.innerHTML.trim() }
    })()
    // Don't cache failures
    promise.catch(() => viewCache.delete(key))
    viewCache.set(key, promise)
  }
  return viewCache.get(key)
}

// Product data for the store (variants, availability, prices)
const getProduct = async (handle) => (await fetchProductView(handle)).data

// Theme product card markup for display, pinned to the item's variant
const getProductCard = async (handle, variantId) => (await fetchProductView(handle, variantId)).cardHtml

// Saves the wishlist to the customer metafield via the app proxy
const saveList = async (lists, uuid) => {
  try {
    if (!shopifyCustomer?.id) {
      console.warn('[Dtails Wishlist] Cannot save wishlist: customer not logged in')
      return { success: false, error: 'Customer not logged in' }
    }

    const url = `${PROXY_API_URL}/update-list?logged_in_customer_id=${encodeURIComponent(shopifyCustomer.id)}`
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: {
          lists: lists.map(list => ({ ...list, items: list.items.map(item => ({ ...item, data: null })) })),
          uuid,
        },
      }),
    })
    if (!response.ok) {
      throw new Error(`Save failed (${response.status})`)
    }
    return { success: true, data: await response.json() }
  } catch (error) {
    console.error('[Dtails Wishlist] Error saving wishlist:', error)
    return { error }
  }
}

const wishlistApi = { saveList, getProduct, getProductCard }

// ==========================================================================
// Stores (wishlist + modal state)
// ==========================================================================

// ---- global-modal.js ----
// Minimal observable modal state. Only one modal is open at a time,
// matching the previous behavior of closing all modals before opening one.
class GlobalModalStore {
  #current = null
  #subscribers = new Set()

  subscribe(callback) {
    this.#subscribers.add(callback)
    return () => this.#subscribers.delete(callback)
  }

  #notify() {
    for (const callback of this.#subscribers) callback()
  }

  openModal(id) {
    this.#current = id
    this.#notify()
  }

  closeModal(id) {
    if (this.#current !== id) return
    this.#current = null
    this.#notify()
  }

  modalOpen(id) {
    return this.#current === id
  }
}

const globalModalStore = new GlobalModalStore()

// ---- wishlist.js ----

// Same localStorage key and shape as the previous pinia-plugin-persistedstate setup,
// so existing guest wishlists survive the migration
const STORAGE_KEY = '__wishlist-app__wishlist'

const generateUuid = () => {
  if (window.crypto?.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

// The account's saved lists, from the customer metafield the app backend writes
// as { lists, uuid }. null when the customer has none yet; false when the value
// has a shape this code doesn't recognise, so nothing may be saved over it
const getWishlistMetafield = () => {
  const data = window.wishlistData?.liquidData?.customer?.wishlist
  if (!data) return null
  return Array.isArray(data.lists) ? data : false
}

// What a save writes, without the cached product data, so a save's result can be
// matched against the lists as they are once it returns
const listsSnapshot = (lists, uuid) => JSON.stringify({
  uuid,
  lists: lists.map(list => ({
    id: list.id,
    name: list.name,
    items: list.items.map(item => [item.handle, String(item.variantId)]),
  })),
})

// Account lists first, then the guest's. Empty guest lists are dropped when the account
// already has lists: signing out leaves an auto-created empty default list in local
// state, and without this every sign-in appended it to the account. Duplicate ids and
// duplicate empty lists with the same name collapse into one, which also cleans accounts
// that already collected them. Empty lists with a unique name are deliberate and stay
const mergeGuestLists = (accountLists, guestLists) => {
  const guests = accountLists.length > 0
    ? guestLists.filter(list => list.items?.length > 0)
    : guestLists
  const merged = []
  for (const list of accountLists.concat(guests)) {
    if (merged.some(kept => kept.id === list.id)) continue
    const emptyTwin = !list.items?.length &&
      merged.some(kept => !kept.items?.length && kept.name?.toLowerCase() === list.name?.toLowerCase())
    if (emptyTwin) continue
    merged.push(list)
  }
  return merged
}

class WishlistStore {
  lists = []
  uuid = generateUuid()
  guest = true
  // True while a lists change has not been confirmed saved to the account. Persisted, so
  // a save lost to a closed tab or a dropped connection is retried on the next load
  // rather than overwritten by the account's copy
  dirty = false
  viewingListId = null
  recentlyRemoved = ''
  recentlyAdded = {}
  #subscribers = new Set()
  #saving = null
  #saveAgain = false

  constructor() {
    this.#restore()
  }

  // Subscribers receive { listsChanged } so persistence-worthy changes can be told
  // apart from view state changes (viewingListId, notices)
  subscribe(callback) {
    this.#subscribers.add(callback)
    return () => this.#subscribers.delete(callback)
  }

  #notify(event = {}) {
    for (const callback of this.#subscribers) callback(event)
  }

  // Persist and notify after any change to lists/uuid/guest. Changes to the lists
  // themselves mark the store dirty until a save confirms them
  #commit({ markDirty = true } = {}) {
    if (markDirty) this.dirty = true
    this.#persist()
    this.#notify({ listsChanged: true })
  }

  #persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        lists: this.lists,
        uuid: this.uuid,
        guest: this.guest,
        dirty: this.dirty,
      }))
    } catch (e) {
      console.warn('[Dtails Wishlist] Failed to persist wishlist locally', e)
    }
  }

  #restore() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY))
      if (stored && typeof stored === 'object') {
        this.lists = stored.lists || []
        this.uuid = stored.uuid || this.uuid
        this.guest = stored.guest ?? true
        this.dirty = stored.dirty ?? false
      }
    } catch (e) { /* corrupt or missing state, start fresh */ }
  }

  reset() {
    this.lists = []
    this.uuid = generateUuid()
    this.guest = true
    this.dirty = false
    this.viewingListId = null
    this.recentlyRemoved = ''
    this.recentlyAdded = {}
    this.#commit({ markDirty: false })
  }

  setGuest(guest) {
    this.guest = guest
    this.#commit({ markDirty: false })
  }

  saveWishlistToUser() {
    // Save wishlist to customer metafields
    // Runs throttled on lists changes, so it's only called once per 5 seconds.
    // One request at a time: on a slow backend a later save could land first and
    // then be overwritten by an older one. A save asked for meanwhile runs once
    // the current one ends, with the lists as they are by then
    if (this.#saving) {
      this.#saveAgain = true
      return this.#saving
    }
    this.#saving = (async () => {
      try {
        do {
          this.#saveAgain = false
          const snapshot = listsSnapshot(this.lists, this.uuid)
          const result = await wishlistApi.saveList(this.lists, this.uuid)
          if (result.error) {
            console.error('[Dtails Wishlist] Failed to save wishlist:', result.error)
          } else if (result.success && listsSnapshot(this.lists, this.uuid) === snapshot) {
            // Nothing changed while the request was out, so the account now matches
            this.dirty = false
            this.#persist()
          }
        } while (this.#saveAgain)
      } finally {
        this.#saving = null
      }
    })()
    return this.#saving
  }

  // Folds the account's saved lists into the local ones, in memory only, so the
  // lists, the header badge and the hearts never wait on the app backend.
  // Returns whether the account needs saving; setupUser does the save
  mergeLists(customerWishlist) {
    if (customerWishlist && this.uuid === customerWishlist.uuid) {
      // This browser last synced with this very account. Unsaved local changes (a save
      // that failed or never got out) still win and are retried. Otherwise the account
      // is the source of truth, so changes made elsewhere (another device, an import
      // into the metafield) show up here instead of being overwritten by a stale copy
      if (this.dirty) return true
      this.lists = customerWishlist.lists
      this.#commit({ markDirty: false })
      return false
    }
    // A guest wishlist meets the account (first sign-in on this browser): keep both
    if (customerWishlist) {
      this.lists = mergeGuestLists(customerWishlist.lists, this.lists)
      this.uuid = customerWishlist.uuid
    }
    this.#commit()
    return true
  }

  async refreshListData() {
    const handles = this.allItems.reduce((acc, item) => {
      // If item data is missing or older than 10 minutes, fetch new data
      if (!item.data) {
        acc.push(item.handle)
      } else {
        const updatedAt = new Date(item.updated_at)
        const tenMinutesAgo = new Date(new Date().getTime() - 10 * 60000)
        if (updatedAt < tenMinutesAgo) {
          acc.push(item.handle)
        }
      }
      return acc
    }, [])
    if (handles.length === 0) return
    const allData = await Promise.allSettled([...new Set(handles)].map(handle => wishlistApi.getProduct(handle)))
    let removed = 0
    this.lists.forEach(list => {
      list.items = list.items.filter(item => {
        const data = allData.find(d => d.value?.handle === item.handle)?.value
        if (data) {
          const variant = data.variants.find(v => v.id == item.variantId)
          // Variant no longer exists, remove from list
          if (!variant) {
            removed++
            return false
          }
          item.data = data
          item.updated_at = new Date().toISOString()
          return true
        }
        // Product no longer exists (rejected with 404), remove from list
        const notFound = allData.find(d => d.reason?.status === 404 && d.reason?.handle === item.handle)
        if (notFound) removed++
        return !notFound
      })
    })
    // Refreshed product data is a cache, only dropped items are a change to the account
    this.#commit({ markDirty: removed > 0 })
  }

  createList(name) {
    const id = generateUuid()
    this.lists.unshift({
      id,
      name,
      items: []
    })
    this.#commit()
    return id
  }

  renameList(listId, name) {
    const list = this.getList(listId)
    if (!list) return
    list.name = name
    this.#commit()
  }

  async addToWishlist(handle, data, listId, variantId) {
    if (!listId) {
      listId = this.createList('Default')
    }
    const list = this.lists.find(list => list.id === listId)
    if (list) {
      variantId = variantId || data.variants.find(variant => variant.available)?.id || data.variants[0].id
      list.items.unshift({
        handle,
        data,
        variantId,
        added_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      this.recentlyAdded = this.getList(list.id)
      this.#commit()

      window.document.dispatchEvent(new CustomEvent('DTAILS_WISHLIST:wishlist:add', {
        detail: {
          productHandle: handle,
          variantId,
        }
      }))
    }
  }

  removeFromWishlist(handle, variantId, listId) {
    // Find all lists where handle exists
    // If listId is provided, only remove from that list (list view is the only place
    // where you know which list to remove from)
    let lists = []
    if (listId) {
      lists = this.lists.filter(list => list.id === listId)
    } else {
      lists = this.lists.filter(list => list.items.find(item => item.handle === handle))
    }
    if (lists.length > 0) {
      this.recentlyRemoved = lists[0].items.find(item => item.handle === handle)?.data?.title

      // On collection pages we don't have variantId, so match on handle only and
      // remove all items with that handle
      lists.forEach(list => {
        const items = list.items.filter(item => item.handle === handle && (variantId ? item.variantId == variantId : true))
        items.forEach(item => {
          list.items.splice(list.items.findIndex(i => i === item), 1)
        })
        if (list.items.length === 0) {
          const listIndex = this.lists.findIndex(l => l.id === list.id)
          this.lists.splice(listIndex, 1)
        }
      })
      this.#commit()

      window.document.dispatchEvent(new CustomEvent('DTAILS_WISHLIST:wishlist:remove', {
        detail: {
          productHandle: handle,
          variantId,
        }
      }))
    }
  }

  moveToList(handle, variantId, toListId, fromListId) {
    const fromList = this.lists.find(list => list.id === fromListId)
    const toList = this.lists.find(list => list.id === toListId)
    const item = fromList.items.find(item => item.handle === handle && (variantId ? item.variantId == variantId : true))
    if (item) {
      toList.items.unshift(item)
      fromList.items.splice(fromList.items.findIndex(i => i === item), 1)
    }
    if (fromList.items.length === 0) {
      const listIndex = this.lists.findIndex(l => l.id === fromListId)
      this.lists.splice(listIndex, 1)
    }
    this.#commit()
  }

  deleteList(listId) {
    const listIndex = this.lists.findIndex(list => list.id === listId)
    this.lists.splice(listIndex, 1)
    this.#commit()
  }

  setViewingList(id) {
    this.viewingListId = id
    this.#notify()
  }

  openViewingList(id) {
    this.viewingListId = id
    globalModalStore.openModal('wishlist')
    this.#notify()
  }

  clearRecentlyAdded() {
    this.recentlyAdded = {}
    this.#notify()
  }

  clearRecentlyRemoved() {
    this.recentlyRemoved = ''
    this.#notify()
  }

  get allItems() {
    return this.lists.flatMap(list => list.items)
  }

  get count() {
    return this.allItems.length
  }

  listContains(listId, productHandle, variantId) {
    const list = this.lists.find(list => list.id === listId)
    if (list) {
      return list.items.find(item => item.handle === productHandle && (variantId ? item.variantId == variantId : true))
    }
    return false
  }

  listsContain(productHandle, variantId) {
    return this.allItems.find(item => {
      return item.handle === productHandle && (variantId ? item.variantId == variantId : true)
    }) || false
  }

  getList(listId) {
    return this.lists.find(list => list.id === listId)
  }
}

const wishlistStore = new WishlistStore()

// ==========================================================================
// Core components (base element, icons, modal shell)
// ==========================================================================

// ---- base-element.js ----
// All storefront components render in light DOM so theme CSS (fonts, colors,
// merchant button classes) applies directly to our markup
class LightElement extends LitElement {
  createRenderRoot() {
    return this
  }
}

// ---- icons.js ----
// Theme icons: rendered server-side from the theme's own snippets/icon-*.liquid
// by wishlist-data.liquid and captured into wishlistData.templates.icons — ONE
// source of truth, nothing transcribed to drift when a glyph is redrawn. The
// wrapper span carries the sizing class (.wl-icon etc.); wishlist.css scales
// the inner svg to fill it. The heroicons below have no theme snippet.
const themeIcon = (name) => (cssClass) => {
  const markup = wishlistData.templates?.icons?.[name]
  return markup ? html`<span class="wl-theme-icon ${cssClass || ''}">${unsafeHTML(markup)}</span>` : nothing
}
const xMarkIcon = themeIcon('close')
const arrowLeftIcon = themeIcon('arrow_left')
const trashIcon = themeIcon('trash')
const chevronDownIcon = themeIcon('chevron_down')
const chevronRightIcon = themeIcon('chevron_right')
const plusIcon = themeIcon('plus')
const checkIcon = themeIcon('check')
const userIcon = themeIcon('customer')
const linkIcon = themeIcon('copy')

// The theme's signed-in customer glyph (checkmark box) has NO snippet: it only
// exists as the CSS mask main.css paints over <shopify-account> when logged in,
// so the artwork is transcribed from that mask (the one unavoidable copy).
const userLoggedInIcon = (cssClass) => html`
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false" role="presentation" class=${cssClass || ''}><path d="M24 25.8C28.62 25.8 32.39 22.04 32.39 17.41C32.39 12.78 28.63 9.02002 24 9.02002C19.37 9.02002 15.61 12.78 15.61 17.41C15.61 22.04 19.37 25.8 24 25.8ZM24 10.93C27.58 10.93 30.49 13.84 30.49 17.42C30.49 21 27.58 23.91 24 23.91C20.42 23.91 17.51 21 17.51 17.42C17.51 13.84 20.42 10.93 24 10.93Z" fill="currentColor"/><path d="M25.8 29.79H8.96001L7.79001 36.42H9.72001L10.56 31.69H25.8V29.79Z" fill="currentColor"/><path d="M28.2 29.79V43.28H41.69V29.79H28.2ZM34.85 39.88H33.76L31.32 37.45L32.13 36.64L34.24 38.75L38.04 33.38L38.98 34.05L34.84 39.89L34.85 39.88Z" fill="currentColor"/></svg>`

// Heroicons (no theme equivalent exists for these)
const outline = (path, cssClass) => html`
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" class=${cssClass || ''}>
    <path stroke-linecap="round" stroke-linejoin="round" d=${path} />
  </svg>`

const pencilSquareIcon = (cssClass) => outline('m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10', cssClass)
const shareIcon = (cssClass) => outline('M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z', cssClass)
const envelopeIcon = (cssClass) => outline('M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75', cssClass)
const devicePhoneMobileIcon = (cssClass) => outline('M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3', cssClass)
const moveToListIcon = (cssClass) => outline('M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9', cssClass)


const heartIcon = () => html`
  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart wishlist-icon" viewBox="0 0 48 48" fill="currentColor">
    <path d="M23.4,37.27h1.23l12.15-12.17c1.29-1.4,2.03-3.37,2.03-5.38,0-4.54-3.69-8.23-8.23-8.23-1.64,0-3.25.5-4.61,1.41l.99,1.65c1.06-.74,2.32-1.16,3.62-1.16,3.49,0,6.33,2.84,6.33,6.33,0,1.55-.56,3.04-1.5,4.07l-11.4,11.41-10.48-10.35c-1.57-1.5-2.44-3.32-2.44-5.13,0-3.49,2.84-6.33,6.33-6.33s5.74,2.74,6.28,5.59h1.93c-.57-3.76-3.53-7.49-8.21-7.49s-8.23,3.69-8.23,8.23c0,2.33,1.08,4.64,3.01,6.49l11.19,11.06Z"/>
  </svg>`

const spinnerIcon = (cssClass) => html`
  <svg role="status" class="wl-spinner ${cssClass || ''}" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" opacity="0.25"/>
    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
  </svg>`

// ---- modal.js ----
// Base class for modal components built on the native <dialog> element,
// which provides focus trapping, ESC handling and top-layer rendering.
// Subclasses set `modalId` and render via `this.renderModal({...})`.
class ModalElement extends LightElement {
  modalId = null

  connectedCallback() {
    super.connectedCallback()
    this.unsubscribeModalStore = globalModalStore.subscribe(() => this.requestUpdate())
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribeModalStore?.()
  }

  get modalIsOpen() {
    return globalModalStore.modalOpen(this.modalId)
  }

  closeModal() {
    globalModalStore.closeModal(this.modalId)
  }

  // Hook for subclasses to reset state when the modal closes
  onModalClosed() {}

  updated() {
    const dialog = this.querySelector('dialog')
    if (!dialog) return
    if (this.modalIsOpen && !dialog.open) {
      dialog.showModal()
    } else if (!this.modalIsOpen && dialog.open) {
      dialog.close()
    }
  }

  // The dialog is the full-viewport scroll container, so a click on it (outside
  // the panel) means backdrop. Two guards: the press must also have started on
  // the backdrop (dragging a text selection out of the panel must not close),
  // and clicks on the dialog's own scrollbar (outside the client area) don't count.
  #pointerDownOnBackdrop = false

  #isBackdropPress(event) {
    const dialog = event.currentTarget
    return event.target === dialog
      && event.offsetX >= 0 && event.offsetX < dialog.clientWidth
      && event.offsetY >= 0 && event.offsetY < dialog.clientHeight
  }

  #handlePointerDown(event) {
    this.#pointerDownOnBackdrop = this.#isBackdropPress(event)
  }

  #handleBackdropClick(event) {
    if (this.#pointerDownOnBackdrop && this.#isBackdropPress(event)) {
      this.closeModal()
    }
    this.#pointerDownOnBackdrop = false
  }

  // Fires on ESC and on dialog.close(); keeps the store in sync either way
  #handleDialogClose() {
    if (this.modalIsOpen) {
      globalModalStore.closeModal(this.modalId)
    }
    this.onModalClosed()
  }

  renderModal({ title, size = 'sm', menu = null, content }) {
    return html`
      <dialog
        class="wl-modal wl-modal--${size} wish__ext"
        @pointerdown=${this.#handlePointerDown}
        @click=${this.#handleBackdropClick}
        @close=${this.#handleDialogClose}>
        <div class="wl-modal__panel">
          ${menu ? html`<div class="wl-modal__menu">${menu}</div>` : nothing}
          <button @click=${this.closeModal} type="button" class="wl-modal__close" aria-label=${t('general.close')}>
            ${xMarkIcon('wl-icon-lg')}
          </button>
          <h3 class="wl-modal__title ${menu ? 'wl-modal__title--with-menu' : ''}">${title}</h3>
          ${content}
        </div>
      </dialog>`
  }
}

// ==========================================================================
// Product item, list actions, notices
// ==========================================================================

// ---- product-item.js ----
class WishlistProductItem extends LightElement {
  static properties = {
    item: { attribute: false },
    listId: { attribute: false },
    publicList: { type: Boolean, attribute: 'public' },
    hasAdded: { state: true },
    addingToCart: { state: true },
    movePopoverOpen: { state: true },
    cardHtml: { state: true },
    cardError: { state: true },
  }

  constructor() {
    super()
    this.publicList = false
    this.hasAdded = false
    this.addingToCart = false
    this.movePopoverOpen = false
    this.iconTemplate = wishlistData.templates?.icon?.trim()
    this.handleDocumentClick = (event) => {
      if (this.movePopoverOpen && !event.composedPath().includes(this)) {
        this.movePopoverOpen = false
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    // The host is the card container: js-wishlist-card opts into the theme's
    // wishlist heart styling, exactly like the mini-cards
    this.classList.add('js-wishlist-card')
    this.setAttribute('data-product-card', '')
    this.unsubscribeStore = wishlistStore.subscribe(() => this.requestUpdate())
    document.addEventListener('click', this.handleDocumentClick)
    // The theme's real product card, pinned to this item's variant
    getProductCard(this.item.handle, this.item.variantId)
      .then((cardHtml) => { this.cardHtml = cardHtml })
      .catch((error) => {
        this.cardError = true
        console.warn('[Dtails Wishlist] Falling back to basic card:', error)
      })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribeStore?.()
    document.removeEventListener('click', this.handleDocumentClick)
  }

  get product() {
    return this.item.data
  }

  get variant() {
    return this.product?.variants?.find(v => v.id == this.item.variantId)
  }

  get movableLists() {
    return wishlistStore.lists?.filter(list => list.id != this.listId && !wishlistStore.listContains(list.id, this.item.handle, this.variant?.id)) || []
  }

  async addToCart() {
    try {
      this.addingToCart = true
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: 1, id: this.variant.id }),
      })
      if (!response.ok) throw new Error(`Add to cart failed (${response.status})`)
      const data = await response.json()
      // Dispatch event for themes to react to (e.g. open a cart drawer)
      document.dispatchEvent(new CustomEvent('DTAILS_WISHLIST:cart:variant-add', { detail: data }))
      // Themes without an event listener need the redirect for visible feedback
      if (wishlistData.settings?.redirect_to_cart !== false) {
        window.location.href = `${window.Shopify.routes.root}cart`
        return
      }
      this.hasAdded = true
      setTimeout(() => {
        this.hasAdded = false
      }, 6000)
    } catch (error) {
      console.error('[Dtails Wishlist] ', error)
    } finally {
      this.addingToCart = false
    }
  }

  renderRemoveButton() {
    const isActive = !!wishlistStore.listsContain(this.item.handle, this.variant?.id)
    return html`
      <button
        @click=${() => wishlistStore.removeFromWishlist(this.item.handle, this.variant?.id, this.listId)}
        type="button"
        aria-label=${t('button.label_remove')}
        class="button-wishlist button-grid ${isActive ? 'button-wishlist--active' : ''}">
        ${this.iconTemplate ? unsafeHTML(this.iconTemplate) : heartIcon()}
      </button>`
  }

  renderMovePopover() {
    return html`
      <div class="wl-popover">
        <button
          type="button"
          class="wl-btn wl-btn--icon"
          aria-label=${t('general.move_to_list')}
          @click=${() => { this.movePopoverOpen = !this.movePopoverOpen }}>
          ${moveToListIcon('wl-icon')}
        </button>
        ${this.movePopoverOpen ? html`
          <div class="wl-popover__panel">
            <p class="wl-popover__header">${t('general.move_to_list')}</p>
            <div class="wl-popover__items">
              ${this.movableLists.map(list => html`
                <div
                  class="wl-popover__item"
                  @click=${() => { this.movePopoverOpen = false; wishlistStore.moveToList(this.item.handle, this.variant?.id, list.id, this.listId) }}>
                  ${list.name}
                </div>`)}
            </div>
          </div>` : nothing}
      </div>`
  }

  renderAddToCart() {
    const variant = this.variant
    const themeButtonClasses = wishlistData.settings?.button_classes?.trim()
    const label = this.hasAdded
      ? html`<span class="wl-inline-center">${t('general.added_to_cart')} ${checkIcon('wl-icon')}</span>`
      : html`<span>${variant?.available ? t('general.add_to_cart') : t('general.sold_out')}</span>`
    return html`
      <button
        @click=${this.addToCart}
        ?disabled=${!variant?.available || this.addingToCart}
        type="button"
        class="wl-add-to-cart ${themeButtonClasses || 'wl-btn wl-btn--accent'}">
        ${label}
        ${this.addingToCart ? spinnerIcon('wl-spinner--btn') : nothing}
      </button>`
  }

  // Fallback while the theme card loads (or if the view fetch fails):
  // a minimal image + title card from the stored product data
  renderBasicCard() {
    const product = this.product
    // Metafield items carry no product data until refreshListData fills it in
    if (!product) return this.renderSkeleton()
    const image = itemImage(this.item)
    return html`
      <div class="wl-card-fallback">
      <div class="wl-card__media">
        ${image ? html`
          <a href=${'/products/' + product.handle} class="wl-card__image-link">
            <img
              src=${imageSrc(image, '600x')}
              alt=${product.title}
              class="wl-card__image"
              loading="lazy" />
          </a>` : html`
          <a href=${'/products/' + product.handle} class="wl-card__image-link wl-card__image-link--empty"></a>`}
      </div>
      <div class="wl-card__info">
        <h3 class="wl-card__title">
          <a href=${'/products/' + product.handle}>${product.title}</a>
        </h3>
        ${this.variant && this.variant.title !== 'Default Title' ? html`
          <p class="wl-card__variant-title">${this.variant.title}</p>` : nothing}
      </div>
      </div>`
  }

  // Placeholder while the theme card loads: rendering the basic card here
  // would paint image+title first and swap in the full card a beat later,
  // which reads as a flicker — one clean paint instead
  renderSkeleton() {
    return html`
      <div class="wl-card-skeleton" aria-hidden="true">
        <div class="wl-card-skeleton__media"></div>
        <div class="wl-card-skeleton__line"></div>
        <div class="wl-card-skeleton__line wl-card-skeleton__line--short"></div>
      </div>`
  }

  render() {
    return html`
      ${this.cardHtml ? unsafeHTML(this.cardHtml) : this.cardError ? this.renderBasicCard() : this.renderSkeleton()}
      ${!this.publicList ? this.renderRemoveButton() : nothing}
      ${!this.cardHtml && !wishlistData.settings?.disable_add_to_cart ? this.renderAddToCart() : nothing}
      ${!this.publicList && this.movableLists.length > 0 ? this.renderMovePopover() : nothing}`
  }
}

customElements.define('wishlist-product-item', WishlistProductItem)

// ---- single-actions.js ----
class WishlistSingleActions extends LightElement {
  static properties = {
    listId: { attribute: false },
    openMenu: { state: true },
    confirmingDelete: { state: true },
  }

  constructor() {
    super()
    this.openMenu = null
    this.confirmingDelete = false
    this.handleDocumentClick = (event) => {
      if (!event.composedPath().includes(this)) {
        this.openMenu = null
        this.resetDeleteConfirm()
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.classList.add('wl-single-actions')
    document.addEventListener('click', this.handleDocumentClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this.handleDocumentClick)
    clearTimeout(this.confirmTimeout)
  }

  resetDeleteConfirm() {
    clearTimeout(this.confirmTimeout)
    this.confirmingDelete = false
  }

  get shareLink() {
    return `${PROXY_API_URL}/share?uuid=${this.listId}&ref=${shopifyCustomer.id}`
  }

  async copyLink() {
    try {
      await navigator.clipboard.writeText(this.shareLink)
    } catch (err) { /* clipboard unavailable, e.g. permission denied */ }
    this.openMenu = null
  }

  toggleMenu(name) {
    this.openMenu = this.openMenu === name ? null : name
  }

  // Two-step delete: first click arms the button, second click deletes.
  // Disarms after a few seconds or when clicking anywhere else.
  handleDeleteClick() {
    if (!this.confirmingDelete) {
      this.confirmingDelete = true
      this.confirmTimeout = setTimeout(() => { this.confirmingDelete = false }, 4000)
      return
    }
    this.resetDeleteConfirm()
    this.dispatchEvent(new CustomEvent('delete'))
  }

  renderDeleteButton() {
    return html`
      <button
        type="button"
        class="wl-menu__button ${this.confirmingDelete ? 'wl-menu__button--danger' : ''}"
        aria-label=${t('modal_list.menu_delete')}
        @click=${this.handleDeleteClick}>
        ${trashIcon('wl-icon')}
        ${this.confirmingDelete ? html`<span class="wl-text-sm">${t('modal_list.confirm_delete')}</span>` : nothing}
      </button>`
  }

  // Guests get a login prompt instead of the share options, so the button
  // explains itself rather than sitting disabled
  renderShareLoginPrompt() {
    return html`
      <a class="wl-menu__item" href=${shopifyRoot + 'account'}>
        ${userIcon('wl-icon')}
        ${t('modal_list.share_login_prompt')}
      </a>`
  }

  renderShareMenu() {
    return html`
      <div class="wl-menu">
        <button type="button" class="wl-menu__button" @click=${() => this.toggleMenu('share')} aria-label=${t('modal_list.share')}>
          ${shareIcon('wl-icon')}
        </button>
        ${this.openMenu === 'share' && !shopifyCustomer ? html`
          <div class="wl-menu__items wl-menu__items--wide">
            ${this.renderShareLoginPrompt()}
          </div>` : nothing}
        ${this.openMenu === 'share' && shopifyCustomer ? html`
          <div class="wl-menu__items">
            <button type="button" class="wl-menu__item" @click=${this.copyLink}>
              ${linkIcon('wl-icon')}
              ${t('modal_list.menu_copy_link')}
            </button>
            <a
              class="wl-menu__item"
              href=${'mailto:?subject=' + t('modal_list.menu_email_subject') + '&body=' + t('modal_list.menu_email_body') + ' ' + encodeURIComponent(this.shareLink)}>
              ${envelopeIcon('wl-icon')}
              ${t('modal_list.menu_email')}
            </a>
            <a
              class="wl-menu__item"
              href=${'sms://?&body=' + t('modal_list.menu_email_body') + ' ' + encodeURIComponent(this.shareLink)}>
              ${devicePhoneMobileIcon('wl-icon')}
              ${t('modal_list.menu_sms')}
            </a>
          </div>` : nothing}
      </div>`
  }

  render() {
    return html`
      ${this.renderDeleteButton()}
      ${this.renderShareMenu()}`
  }
}

customElements.define('wishlist-single-actions', WishlistSingleActions)

// ---- notice.js ----
const NOTICE_DURATION = 6000

class WishlistNotice extends LightElement {
  #lastAdded = null
  #lastRemoved = null
  #addedTimeout = null
  #removedTimeout = null

  connectedCallback() {
    super.connectedCallback()
    this.unsubscribeStore = wishlistStore.subscribe(() => {
      this.#syncTimers()
      this.requestUpdate()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribeStore?.()
    clearTimeout(this.#addedTimeout)
    clearTimeout(this.#removedTimeout)
  }

  // Only one notice shows at a time; a new add/remove replaces the other and restarts the timer
  #syncTimers() {
    const added = wishlistStore.recentlyAdded
    const removed = wishlistStore.recentlyRemoved
    if (added?.id && added !== this.#lastAdded) {
      this.#lastAdded = added
      clearTimeout(this.#addedTimeout)
      clearTimeout(this.#removedTimeout)
      if (removed) wishlistStore.clearRecentlyRemoved()
      this.#addedTimeout = setTimeout(() => {
        this.#lastAdded = null
        wishlistStore.clearRecentlyAdded()
      }, NOTICE_DURATION)
    }
    if (removed && removed !== this.#lastRemoved) {
      this.#lastRemoved = removed
      clearTimeout(this.#addedTimeout)
      clearTimeout(this.#removedTimeout)
      if (wishlistStore.recentlyAdded?.id) wishlistStore.clearRecentlyAdded()
      this.#removedTimeout = setTimeout(() => {
        this.#lastRemoved = null
        wishlistStore.clearRecentlyRemoved()
      }, NOTICE_DURATION)
    }
  }

  render() {
    const added = wishlistStore.recentlyAdded
    const removed = wishlistStore.recentlyRemoved

    if (added?.id) {
      return html`
        <div class="wl-notice">
          <div class="wl-notice__thumb">
            <img src=${imageSrc(itemImage(added.items[0]))} alt=${added.items[0]?.data?.title || ''} class="lazyload">
          </div>
          <div>
            ${!shopifyCustomer ? html`<span class="wl-muted wl-text-sm">${t('modal_add.guest')}</span>` : nothing}
            <div class="wl-notice__text">
              <span>${t('modal_add.saved_to')}</span>
              <span class="wl-notice__list-name" @click=${() => wishlistStore.openViewingList(added.id)}>${added.name}</span>
            </div>
          </div>
        </div>`
    }

    if (removed) {
      return html`
        <div class="wl-notice">
          <div class="wl-notice__text">
            <span>${t('general.removed_from_wishlist')}</span>
            <span class="wl-strong">${removed}</span>
          </div>
        </div>`
    }

    return nothing
  }
}

customElements.define('wishlist-notice', WishlistNotice)

// ==========================================================================
// Lists modal, add-to-list modal, shared list
// ==========================================================================

// ---- lists-modal.js ----
class WishlistListsModal extends ModalElement {
  static properties = {
    editListName: { state: true },
  }

  constructor() {
    super()
    this.modalId = 'wishlist'
    this.editListName = false
    this.originalListName = ''
  }

  connectedCallback() {
    super.connectedCallback()
    this.unsubscribeWishlistStore = wishlistStore.subscribe(() => this.requestUpdate())
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribeWishlistStore?.()
  }

  get viewingList() {
    return wishlistStore.getList(wishlistStore.viewingListId) || {}
  }

  updated() {
    super.updated()
    if (this.modalIsOpen && !this.prefetchedCards) {
      this.prefetchedCards = true
      for (const list of wishlistStore.lists) {
        for (const item of list.items.slice(0, 24)) {
          getProductCard(item.handle, item.variantId).catch(() => {})
        }
      }
    }
    if (!this.modalIsOpen) this.prefetchedCards = false
  }

  get totalAmount() {
    return this.viewingList.items?.reduce((acc, item) => (
      acc + item.data?.variants?.find(v => v.id == item.variantId)?.price
    ), 0)
  }

  onModalClosed() {
    setTimeout(() => {
      wishlistStore.setViewingList(null)
      this.stopEditingName({ restoreIfEmpty: false })
    }, 200)
  }

  willUpdate() {
    // The viewed list can disappear (deleted, or emptied and auto-removed)
    if (wishlistStore.viewingListId && !this.viewingList.id) {
      wishlistStore.setViewingList(null)
      this.stopEditingName({ restoreIfEmpty: false })
    }
  }

  startEditingName() {
    this.originalListName = this.viewingList.name
    this.editListName = true
  }

  stopEditingName({ restoreIfEmpty = true } = {}) {
    if (restoreIfEmpty && this.viewingList.id && !this.viewingList.name) {
      wishlistStore.renameList(this.viewingList.id, this.originalListName)
    }
    this.editListName = false
  }

  renderMenu() {
    return html`
      <div class="wl-modal__account">
        ${shopifyCustomer ? userLoggedInIcon('wl-icon-lg') : userIcon('wl-icon-lg')}
        ${shopifyCustomer
          ? html`<span class="wl-muted wl-text-xs">${unsafeHTML(shopifyCustomer.name)}</span>`
          : html`<span class="wl-muted wl-text-xs"><a class="wl-link" href=${shopifyRoot + 'account'}>${t('modal_list.login')}</a> ${t('modal_list.login_text')}</span>`}
      </div>`
  }

  renderListDetail() {
    const list = this.viewingList
    return html`
      <div>
        <div class="wl-list-header">
          <div class="wl-list-header__name">
            <button type="button" class="wl-icon-button" aria-label=${t('general.close')} @click=${() => wishlistStore.setViewingList(null)}>
              ${arrowLeftIcon('wl-icon-lg')}
            </button>
            ${this.editListName ? html`
              <input
                type="text"
                class="wl-input"
                placeholder=${t('modal_list.name_placeholder')}
                .value=${list.name || ''}
                @input=${(e) => wishlistStore.renameList(list.id, e.target.value)} />
              <button type="button" class="wl-icon-button" @click=${() => this.stopEditingName()}>
                ${checkIcon('wl-icon-lg')}
              </button>` : html`
              <h4 class="wl-list-header__title">
                <button
                  type="button"
                  class="wl-list-header__title-edit"
                  aria-label=${t('modal_list.menu_edit_name')}
                  @click=${() => this.startEditingName()}>
                  ${list.name}
                  ${pencilSquareIcon('wl-icon wl-list-header__edit-icon')}
                </button>
              </h4>`}
          </div>

          <div class="wl-list-header__actions">
            <div class="wl-list-header__total">
              <span class="wl-text-sm">${t('modal_list.total_amount')}:</span>
              <span class="wl-strong">${money(this.totalAmount)}</span>
            </div>
            <wishlist-single-actions
              .listId=${wishlistStore.viewingListId}
              @delete=${() => wishlistStore.deleteList(wishlistStore.viewingListId)}>
            </wishlist-single-actions>
          </div>
        </div>

        <ul class="wl-product-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-theme gap-y-16" role="list">
          ${repeat(list.items || [], item => `${item.handle}:${item.variantId}`, item => html`
            <li><wishlist-product-item .item=${item} .listId=${wishlistStore.viewingListId}></wishlist-product-item></li>`)}
        </ul>
      </div>`
  }

  renderListPreview(list) {
    const layoutClass = (idx) => {
      const rowSpan = list.items.length == 1 || (list.items.length == 3 && idx == 0) || list.items.length == 2
      return `${rowSpan ? 'wl-list-preview__cell--tall' : ''} ${list.items.length == 1 ? 'wl-list-preview__cell--wide' : ''}`
    }
    return html`
      <div class="wl-list-preview" @click=${() => wishlistStore.setViewingList(list.id)}>
        <div class="wl-list-preview__images">
          ${list.items.slice(0, 4).map((item, idx) => html`
            <div class="wl-list-preview__cell ${layoutClass(idx)}">
              ${item.data ? html`<img src=${imageSrc(itemImage(item))} alt=${item.data?.title || ''} class="wl-list-preview__image">` : nothing}
            </div>`)}
        </div>
        <span class="wl-muted wl-text-sm">${list.items.length} ${t('general.product_count', { count: list.items.length })}</span>
        <span class="wl-list-preview__name">${list.name}</span>
      </div>`
  }

  renderOverview() {
    return html`
      <div class="wl-lists-grid">
        ${wishlistStore.lists.map(list => this.renderListPreview(list))}
        ${wishlistStore.lists.length === 0 ? html`
          <div class="wl-empty-state">
            <h3>${t('modal_list.empty_state_title')}</h3>
            <p class="wl-muted">${t('modal_list.empty_state_content')}</p>
          </div>` : nothing}
      </div>`
  }

  render() {
    const content = html`
      <div class="wl-modal__body wl-modal__body--alt">
        ${wishlistStore.viewingListId && this.viewingList.id ? this.renderListDetail() : this.renderOverview()}
        ${shopifyCustomer ? nothing : html`
          <div class="wl-account-info">
            <div>${unsafeHTML(wishlistData.settings?.text_account_info || '')}</div>
            <div class="wl-text-xxs">${unsafeHTML(wishlistData.settings?.text_no_account_info || '')}</div>
          </div>`}
      </div>`
    return this.renderModal({
      title: t('modal_list.title'),
      size: 'lg',
      menu: this.renderMenu(),
      content,
    })
  }
}

customElements.define('wishlist-lists-modal', WishlistListsModal)

// ---- add-to-list-modal.js ----

class WishlistAddModal extends ModalElement {
  static properties = {
    productHandle: { attribute: false },
    variantId: { attribute: false },
    newListName: { state: true },
    toggleNewList: { state: true },
    selectedVariantId: { state: true },
    productData: { state: true },
    fetchingData: { state: true },
  }

  constructor() {
    super()
    this.modalId = 'new-list'
    this.productHandle = ''
    this.variantId = ''
    this.newListName = ''
    this.toggleNewList = false
    this.selectedVariantId = ''
    this.productData = {}
    this.fetchingData = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.unsubscribeWishlistStore = wishlistStore.subscribe(() => this.requestUpdate())
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribeWishlistStore?.()
  }

  onModalClosed() {
    this.newListName = ''
    this.toggleNewList = false
  }

  async #fetchProduct(handle) {
    this.fetchingData = true
    try {
      this.productData = await wishlistApi.getProduct(handle)
    } finally {
      this.fetchingData = false
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('productHandle') && this.productHandle) {
      this.#fetchProduct(this.productHandle)
    }
    if (changedProperties.has('variantId')) {
      this.selectedVariantId = this.variantId
    }
  }

  async addToWishlist(listId) {
    if (!listId && this.newListName) {
      listId = wishlistStore.createList(this.newListName)
      this.newListName = ''
    }
    await wishlistStore.addToWishlist(this.productHandle, this.productData, listId, this.selectedVariantId)
    this.closeModal()
  }

  toggleList(listId) {
    if (wishlistStore.listContains(listId, this.productHandle, this.selectedVariantId)) {
      wishlistStore.removeFromWishlist(this.productHandle, this.selectedVariantId, listId)
    } else {
      this.addToWishlist(listId)
    }
  }

  // The theme's `.select` wrapper draws the chevron (main.css .select::after).
  // A static id is safe: there is one add modal per page (addModalElement)
  renderVariantSelect() {
    return html`
      <div class="wl-variant-select">
        <label class="wl-text-sm" for="wl-variant-select">${t('modal_add.choose_variant')}:</label>
        <div class="select relative">
          <select id="wl-variant-select" class="wl-select" @change=${(e) => { this.selectedVariantId = e.target.value }}>
            ${this.productData.variants.map(variant => html`
              <option value=${variant.id} ?selected=${this.selectedVariantId == variant.id}>${variant.title}</option>`)}
          </select>
        </div>
      </div>`
  }

  renderCreateNewList() {
    if (!this.toggleNewList) {
      return html`
        <div class="wl-create-list" @click=${() => { this.toggleNewList = true }}>
          <button type="button" class="wl-create-list__button">${plusIcon('wl-icon-lg')}</button>
          <span class="wl-strong">${t('modal_add.button_create_new_list')}</span>
        </div>`
    }
    return html`
      <div class="wl-create-list__form">
        <input
          type="text"
          class="wl-input wl-input--tall"
          placeholder=${t('modal_add.new_list_placeholder')}
          .value=${this.newListName}
          @input=${(e) => { this.newListName = e.target.value }} />
        <button
          type="button"
          @click=${() => this.addToWishlist()}
          ?disabled=${!this.newListName}
          class="wl-btn wl-btn--accent wl-btn--small">
          ${t('modal_add.button_create')}
        </button>
      </div>`
  }

  renderListRow(list) {
    const added = wishlistStore.listContains(list.id, this.productHandle, this.selectedVariantId)
    return html`
      <div class="wl-list-row">
        <div class="wl-list-row__main">
          <div class="wl-list-row__info">
            <div class="wl-list-row__thumb">
              ${list.items[0]?.data ? html`
                <img src=${imageSrc(itemImage(list.items[0]))} alt=${list.items[0].data.title} class="lazyload">` : nothing}
            </div>
            <span class="wl-strong">${list.name}</span>
          </div>
          <button
            type="button"
            @click=${() => this.toggleList(list.id)}
            class="wl-btn wl-btn--accent wl-btn--small ${added ? 'wl-btn--added wishlist__modal-add--added' : ''}">
            ${added
              ? html`${t('modal_add.button_added')}`
              : html`${plusIcon('wl-icon')} ${t('modal_add.button_add')}`}
          </button>
        </div>
        <div class="wl-list-row__view" @click=${() => wishlistStore.openViewingList(list.id)}>
          <span class="wl-text-xs">${t('general.view_list')}</span>
          ${chevronRightIcon('wl-icon-xs')}
        </div>
      </div>`
  }

  render() {
    const content = html`
      <div class="wl-modal__body">
        ${this.fetchingData ? html`
          <div class="wl-loading wl-loading--compact">${spinnerIcon()}</div>` : html`
          ${this.productData.variants?.length > 1 ? this.renderVariantSelect() : nothing}
          ${this.renderCreateNewList()}
          ${wishlistStore.lists.length > 0 ? html`
            <div class="wl-list-section-title">
              <span class="wl-strong">${t('modal_list.title')}</span>
            </div>` : nothing}
          <div class="wl-list-rows">
            ${wishlistStore.lists.map(list => this.renderListRow(list))}
          </div>`}
      </div>`
    return this.renderModal({
      title: t('modal_add.title'),
      size: 'sm',
      content,
    })
  }
}

customElements.define('wishlist-add-modal', WishlistAddModal)

// ---- shared-list.js ----
// Public, read-only view of a shared list. Mounted on the app proxy page,
// where the server injects wishlistData.sharedList
class WishlistSharedList extends LightElement {
  static properties = {
    hydrated: { state: true },
  }

  constructor() {
    super()
    this.sharedList = sharedList
    this.hydrated = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.#hydrate()
  }

  get totalAmount() {
    return this.sharedList.items.reduce((acc, item) => (
      acc + (item.data?.variants.find(v => v.id == item.variantId)?.price || 0)
    ), 0)
  }

  async #hydrate() {
    if (!this.sharedList.id) return
    await Promise.allSettled(this.sharedList.items.map(async (item) => {
      if (item.data) return
      const data = await getProduct(item.handle)
      item.data = data
      // Make sure variant still exists, if not, set to first variant
      item.variantId = data.variants.find(v => v.id == item.variantId)?.id || data.variants[0].id
    }))
    // Items whose product failed to load (deleted, or a fetch error) can't render
    this.sharedList.items = this.sharedList.items.filter(item => item.data)
    this.hydrated = true
  }

  render() {
    return html`
      <div class="wish__ext">
        <div class="wl-shared">
          <div class="wl-shared__panel">
            ${!this.sharedList.id ? html`
              <h4 class="wl-shared__empty">${t('shared_single.no_result_found')}</h4>` : this.hydrated ? html`
              <h3 class="wl-shared__title">${t('shared_single.title')} <span class="wl-strong">${this.sharedList.name}</span></h3>
              <div class="wl-shared__total">
                <span>${t('modal_list.total_amount')}:</span>
                <span class="wl-strong">${money(this.totalAmount)}</span>
              </div>
              <ul class="wl-product-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-theme gap-y-16" role="list">
                ${repeat(this.sharedList.items, item => `${item.handle}:${item.variantId}`, item => html`
                  <li><wishlist-product-item .item=${item} .listId=${this.sharedList.id} public></wishlist-product-item></li>`)}
              </ul>` : html`
              <div class="wl-loading">${spinnerIcon()}</div>`}
          </div>
        </div>
      </div>`
  }
}

customElements.define('wishlist-shared-list', WishlistSharedList)

// ==========================================================================
// App (wiring: buttons, modals, header, cart bridge)
// ==========================================================================

const buttonActiveClass = 'button-wishlist--active'

const selectors = {
  button: '[data-button-wishlist]',
  trigger: '[data-wishlist-trigger]',
}

let addModalElement = null

function throttle(fn, wait) {
  let last = 0
  let timeout = null
  return (...args) => {
    const now = Date.now()
    const remaining = wait - (now - last)
    clearTimeout(timeout)
    if (remaining <= 0) {
      last = now
      fn(...args)
    } else {
      timeout = setTimeout(() => {
        last = Date.now()
        fn(...args)
      }, remaining)
    }
  }
}

// Wraps every server-rendered wishlist button (cards, PDP) and keeps its
// active state in sync with the store. connectedCallback covers late-arriving
// markup (Relewise tiles, quick views) — no DOM observation needed.
class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector(selectors.button)
    this.unsubscribe = wishlistStore.subscribe(() => this.sync())
    this.sync()
  }

  disconnectedCallback() {
    this.unsubscribe?.()
  }

  sync() {
    if (!this.button) return
    const isActive = !!wishlistStore.listsContain(this.button.dataset.productHandle, this.button.dataset.variantId)
    this.button.classList.toggle(buttonActiveClass, isActive)
    this.button.setAttribute('aria-label', t(isActive ? 'button.label_remove' : 'button.label_add'))

  }
}
customElements.define('wishlist-toggle', WishlistToggle)

function initApp() {
  mountComponents()
  setupUser()
  setupClickDelegation()
  setupVariantChange()
  setupCartDrawerBridge()

  wishlistStore.subscribe(() => updateTriggerCounter())
  updateTriggerCounter()
}

function mountComponents() {
  const mountPoint = document.createElement('div')
  mountPoint.setAttribute('id', 'wishlist-app')
  mountPoint.classList.add('wish__ext')
  addModalElement = document.createElement('wishlist-add-modal')
  mountPoint.append(
    addModalElement,
    document.createElement('wishlist-lists-modal'),
    document.createElement('wishlist-notice'),
  )
  document.body.appendChild(mountPoint)
}

// Authenticate user on first load and set up saving the wishlist to the customer.
// Nothing here waits on the app backend: the merge and the default list happen in
// memory and are saved after. Awaiting the first save used to hold back the default
// list and every later save, so a slow backend left a signed-in customer with an
// empty pop-up (ClickUp 869faw1ue)
async function setupUser() {
  if (shopifyCustomer) {
    wishlistStore.setGuest(false)
    const customerWishlist = getWishlistMetafield()
    if (customerWishlist === false) {
      // Saving the local lists would overwrite account data we can't read
      console.error('[Dtails Wishlist] Unrecognised customer wishlist data, not saving to the account')
    } else {
      let needsSave = wishlistStore.mergeLists(customerWishlist)
      if (window.wishlistData?.settings?.auto_add_to_default_list && wishlistStore.lists.length === 0) {
        // Created before the save subscriber exists, so it must be saved explicitly or the
        // store stays dirty and the empty list wins over the account on the next load
        wishlistStore.createList(t('general.default_list_name') || 'My wishlist')
        needsSave = true
      }
      const throttledSave = throttle(() => wishlistStore.saveWishlistToUser(), 5000)
      wishlistStore.subscribe((event) => {
        if (event.listsChanged) throttledSave()
      })
      if (needsSave) throttledSave()
    }
  } else if (wishlistStore.guest === false) {
    wishlistStore.reset()
  }

  if (window.wishlistData?.settings?.auto_add_to_default_list && wishlistStore.lists.length === 0) {
    wishlistStore.createList(t('general.default_list_name') || 'My wishlist')
  }

  await wishlistStore.refreshListData()
}

function setupClickDelegation() {
  document.addEventListener('click', function (e) {
    const button = e.target.closest(selectors.button)
    if (button) {
      e.preventDefault()
      return buttonHandler(button)
    }
    if (e.target.closest(selectors.trigger)) {
      globalModalStore.openModal('wishlist')
    }
  }, false)
}

function buttonHandler(button) {
  const productHandle = button.dataset.productHandle
  const variantId = button.dataset.variantId
  if (!productHandle) return console.error('[Dtails Wishlist] Missing `data-product-handle` attribute. Failed to update the wishlist.')
  if (wishlistStore.listsContain(productHandle, variantId)) {
    wishlistStore.removeFromWishlist(productHandle, variantId)
  } else {
    addModalElement.productHandle = productHandle
    addModalElement.variantId = variantId
    globalModalStore.openModal('new-list')
  }
}

// Theme event fired by product forms (PDP, quickbuy, featured product):
// keep the wishlist button of that product scope variant-accurate
function setupVariantChange() {
  document.addEventListener('on:variant:change', (event) => {
    const variantId = event.detail?.variant?.id
    if (!variantId) return
    // Only within a product scope — a document-wide update would stamp this
    // product's variant onto other products' buttons
    const scope = event.target?.closest?.('.js-product')
    if (!scope) return
    scope.querySelectorAll(`${selectors.button}[data-variant-id]`).forEach((button) => {
      button.dataset.variantId = variantId
      button.closest('wishlist-toggle')?.sync()
    })
  })
}

function updateTriggerCounter() {
  const triggerCounter = document.querySelector(`${selectors.trigger} [data-wishlist-counter]`)
  if (!triggerCounter) return
  // Capped at "9+" to match the cart badge — Ilva's round bubble (2026-09-15) widens
  // into a pill at two digits. The empty-string branch is load-bearing: the badge is
  // hidden at zero by `.wl-counter:empty { display: none }` in wishlist.css.
  const count = wishlistStore.count
  triggerCounter.innerHTML = count > 9 ? '9+' : count || ''
}

// Bridge to the Enterprise theme's cart drawer: it listens for `on:cart:add`
// and refreshes/opens itself (see assets/custom.js docs)
function setupCartDrawerBridge() {
  document.addEventListener('DTAILS_WISHLIST:cart:variant-add', ({ detail }) => {
    document.dispatchEvent(new CustomEvent('on:cart:add', {
      bubbles: true,
      detail: { variantId: detail?.variant_id || detail?.id },
    }))
  })
  // The drawer can't layer above the top-layer wishlist <dialog>; close the
  // wishlist so the drawer (opened by the theme on this event) is visible
  document.addEventListener('on:cart:add', () => {
    globalModalStore.closeModal('wishlist')
    globalModalStore.closeModal('new-list')
  })
}

initApp()

// Shared list mount point comes from the app proxy page
const sharedListMount = document.querySelector('#wishlist-app-shared-list')
if (sharedListMount) {
  sharedListMount.appendChild(document.createElement('wishlist-shared-list'))
}

